import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useFormContext } from 'react-hook-form';
import logger from '@widgets/logger';
import { useCurrentEditor } from '@tiptap/react';
import { styled } from '@mui/material/styles';
import Button from '@components/Button';
import TextEditor from '@components/TextEditor';
import { parseTipTapJSONToPlainText } from '@helpers/text-editor';
import { handleApiError } from '@helpers/api';
import { useTranslation } from '@hooks';
import { MessageType } from '@constants/inbox';
import { agentsService } from '@services';
import { SendIcon } from 'assets/images/icons';

const StyledTextEditor = styled(TextEditor)({
    paddingBottom: ' 0 !important',
    backgroundColor: '#fff',
    '& .ProseMirror': {
        maxHeight: '48px !important',
        minHeight: 'auto !important',
    },
    '& .editor-container': {
        display: 'grid',
        gridTemplateColumns: 'minmax(auto, calc(100% - 46px)) 38px',
        gridGap: '8px',
        alignItems: 'flex-end',
        padding: '11px',
        minHeight: 'auto',
        '&:focus-within': {
            padding: '10px !important',
        },
    },
    '& .editor-toolbar': {
        bottom: '50% !important',
        right: '12px !important',
        width: 'auto !important',
        left: 'auto !important',
        transform: 'translateY(50%) !important',
    },
});

const StyledButton = styled(Button)({
    height: 22,
    minWidth: 'auto',
    '& .MuiButton-endIcon': {
        transform: 'rotate(90deg)',
        margin: 0,
        '& svg path': {
            fillOpacity: 1,
        },
    },
});

const SendButton = ({ disabled, onClick }) => {
    const { editor } = useCurrentEditor();

    const handleClick = async () => {
        try {
            await onClick(editor);
        } catch (error) {
            logger.error(error);
        }
    };
    return <StyledButton variant="contained" disabled={disabled} onClick={handleClick} endIcon={<SendIcon />} />;
};

const PulseAIInput = ({ selectedAgentRef, setData, conversationIdRef, setIsTyping, isTyping }) => {
    const [message, setMessage] = useState('');

    const { getValues, watch } = useFormContext();
    const { t } = useTranslation();

    const brandId = watch('brandId');
    const locationId = watch('locationId');

    const onClickSendMessage = async editor => {
        try {
            setIsTyping(true);
            const parsedMessage = parseTipTapJSONToPlainText(message);
            const { days, hours, minutes, ...values } = getValues();
            setData(prev => [
                ...prev,
                {
                    id: uuidv4(),
                    message: parsedMessage,
                    createdAt: new Date().toISOString(),
                    type: MessageType.Outgoing,
                },
            ]);
            editor?.commands.clearContent();
            const response = await agentsService.sendPlaygroundMessage({
                conversationId: conversationIdRef.current,
                message: parsedMessage,
                agent: {
                    ...selectedAgentRef.current,
                    ...values,
                    status: Number(values.status),
                    locationId: values.locationId || null,
                    brandId: values.brandId || null,
                    handoff: +values.handoff,
                    autoCloseDelay: `${days}:${hours}:${minutes}`,
                },
            });
            setMessage('');
            const newMessages = response.messages.map(messageText => ({
                id: uuidv4(),
                message: messageText,
                createdAt: new Date().toISOString(),
                type: MessageType.Incoming,
            }));
            setData(prev => [...prev, ...newMessages]);
        } catch (error) {
            handleApiError(error);
            throw error;
        } finally {
            setIsTyping(false);
        }
    };

    return (
        <StyledTextEditor
            initialValue={''}
            onChange={text => setMessage(text)}
            editable={!isTyping}
            containerProps={{
                onKeyDown: async (e, editor) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        if (!isTyping && message && (locationId || brandId)) {
                            await onClickSendMessage(editor);
                        }
                    }
                },
            }}
            placeholder={t('InboxPage.messageLabel')}
            toolbar={
                <SendButton disabled={isTyping || !message || (!locationId && !brandId)} onClick={onClickSendMessage} />
            }
        />
    );
};

export default PulseAIInput;
